/**
 * The shared per-file upload row list — extracted from UploadDialog so the
 * library and template upload dialogs render an identical status list (spinner /
 * error / check icon, name, size, live percent bar, failure reason, remove). The
 * caller owns the item state machine; this only renders it.
 */
import { AlertCircle, CheckCircle, Loader2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { FileUploadItem } from "./types";

interface UploadFileListProps {
    items: FileUploadItem[];
    onRemove: (id: string) => void;
    /** Disable the per-row remove while a confirm/submit is in flight. */
    removeDisabled?: boolean;
}

export function UploadFileList({
    items,
    onRemove,
    removeDisabled = false,
}: UploadFileListProps) {
    if (items.length === 0) return null;
    return (
        <ul className="space-y-2">
            {items.map((item) => (
                <li
                    key={item.id}
                    className={cn(
                        "flex items-center gap-3 rounded-md border p-3",
                        item.status === "failed"
                            ? "border-destructive"
                            : "border-border"
                    )}
                >
                    <div
                        className={cn(
                            "shrink-0",
                            item.status === "ready"
                                ? "text-emerald-600"
                                : item.status === "failed"
                                  ? "text-destructive"
                                  : "text-primary"
                        )}
                    >
                        {item.status === "uploading" ? (
                            <Loader2 className="size-4 animate-spin" />
                        ) : item.status === "failed" ? (
                            <AlertCircle className="size-4" />
                        ) : (
                            <CheckCircle className="size-4" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                            {item.file.name}
                        </p>
                        <p className="text-muted-foreground text-xs">
                            {(item.file.size / (1024 * 1024)).toFixed(2)} MB
                            {item.status === "uploading" && (
                                <>
                                    {" "}
                                    <span className="text-primary font-medium">
                                        · {item.progress}%
                                    </span>
                                </>
                            )}
                            {item.status === "ready" && (
                                <>
                                    {" "}
                                    <span className="font-medium text-emerald-600">
                                        · Ready
                                    </span>
                                </>
                            )}
                            {item.status === "failed" && (
                                <>
                                    {" "}
                                    <span className="text-destructive font-medium">
                                        · Failed
                                    </span>
                                </>
                            )}
                        </p>
                        {item.status === "uploading" && (
                            <div className="bg-muted mt-1 h-1 overflow-hidden rounded-full">
                                <div
                                    className="bg-primary h-full transition-all"
                                    style={{ width: `${item.progress}%` }}
                                />
                            </div>
                        )}
                        {item.status === "failed" && item.errorReason && (
                            <p className="text-destructive mt-0.5 text-xs">
                                {item.errorReason}
                            </p>
                        )}
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => onRemove(item.id)}
                        disabled={removeDisabled}
                        aria-label="Remove file"
                    >
                        <X size={16} />
                    </Button>
                </li>
            ))}
        </ul>
    );
}
